import React, { useState, useEffect } from 'react';
import './SimulationSetup.css';

const SimulationSetup = ({ selectedPortfolio, onSimulationCreated }) => {
    // Основные данные формы
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });

    // Типы симуляций и активная вкладка
    const [simulationTypes, setSimulationTypes] = useState([]);
    const [activeTab, setActiveTab] = useState(null);
    const [typeParams, setTypeParams] = useState({}); // Параметры для каждого типа симуляции

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [isLoadingTypes, setIsLoadingTypes] = useState(true);

    // Загрузка типов симуляции при монтировании компонента
    useEffect(() => {
        const fetchSimulationTypes = async () => {
            try {
                const response = await fetch('/api/v1/simulation-types');
                if (!response.ok) {
                    throw new Error('Failed to fetch simulation types');
                }
                const data = await response.json();
                setSimulationTypes(data.simulation_types);

                // Устанавливаем первый тип как активный по умолчанию
                if (data.simulation_types.length > 0) {
                    const firstType = data.simulation_types[0];
                    setActiveTab(firstType.id);

                    // Инициализируем параметры для всех типов из parameters_schema
                    const initialTypeParams = {};
                    data.simulation_types.forEach(type => {
                        const defaults = {};
                        if (type.parameters_schema && type.parameters_schema.parameters) {
                            type.parameters_schema.parameters.forEach(param => {
                                if (param.default_value !== undefined && param.default_value !== null) {
                                    defaults[param.key] = param.default_value;
                                }
                            });
                        }
                        initialTypeParams[type.id] = defaults;
                    });
                    setTypeParams(initialTypeParams);
                }

                setIsLoadingTypes(false);
            } catch (error) {
                console.error('Error loading simulation types:', error);
                setErrors({ loadTypes: 'Не удалось загрузить типы симуляций' });
                setIsLoadingTypes(false);
            }
        };

        fetchSimulationTypes();
    }, []);

    const handleInputChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleTypeParamChange = (paramName, value) => {
        setTypeParams(prev => ({
            ...prev,
            [activeTab]: {
                ...prev[activeTab],
                [paramName]: value
            }
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Название обязательно';
        }

        // Базовая валидация - дополнительную валидацию параметров можно добавить позже

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedPortfolio) {
            setErrors({ submit: 'Портфель не выбран' });
            return;
        }

        if (!activeTab) {
            setErrors({ submit: 'Тип симуляции не выбран' });
            return;
        }

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        try {
            // Формируем данные в новом формате с type_id и type_params
            const simulationData = {
                name: formData.name,
                description: formData.description,
                portfolio_id: selectedPortfolio.id,
                type_id: activeTab, // ID активной вкладки
                type_params: typeParams[activeTab] || {} // Параметры активного типа
            };

            const response = await fetch(`/api/v1/simulations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(simulationData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Ошибка при создании симуляции');
            }

            const result = await response.json();
            console.log('Simulation created:', result);

            // Уведомляем родительский компонент об успешном создании
            if (onSimulationCreated) {
                onSimulationCreated(result);
            }

            // Сбрасываем форму
            setFormData({
                name: '',
                description: '',
                start_date: '2020-01-01',
                end_date: '2024-01-01',
                enable_rebalancing: false,
                rebalance_frequency: 'monthly',
                rebalance_threshold: 5.0,
                transaction_cost: 0.1,
                calculate_inflation: false,
                inflation_base_includes_first_loan: false,
                enable_margin: false,
                margin_target_pct: 50.0,
                margin_interest_rate: 7.0,
                min_margin_loan: 1000.0,
                enable_buy_borrow_die: false,
                income_share: 4.0
            });

            setErrors({});
        } catch (error) {
            console.error('Error creating simulation:', error);
            setErrors({ submit: error.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Функция для генерации поля формы на основе параметра из схемы
    const renderFormField = (param) => {
        const currentValue = typeParams[activeTab]?.[param.key];
        const paramKey = param.key;
        const paramType = param.type;
        const label = param.label || formatParamName(param.key);
        const description = param.description;
        const required = param.required;

        // Определяем тип поля по type из схемы
        if (paramType === 'boolean') {
            // Boolean -> checkbox
            return (
                <div className="form-group" key={paramKey}>
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={currentValue || false}
                            onChange={(e) => handleTypeParamChange(paramKey, e.target.checked)}
                        />
                        <span>{label}{required ? ' *' : ''}</span>
                    </label>
                    {description && <small className="field-description">{description}</small>}
                </div>
            );
        } else if (paramType === 'date') {
            // Date fields
            return (
                <div className="form-group" key={paramKey}>
                    <label htmlFor={paramKey}>{label}{required ? ' *' : ''}</label>
                    <input
                        type="date"
                        id={paramKey}
                        value={currentValue || ''}
                        onChange={(e) => handleTypeParamChange(paramKey, e.target.value)}
                    />
                    {description && <small className="field-description">{description}</small>}
                </div>
            );
        } else if (paramType === 'enum' && param.enum_options) {
            // Enum -> select dropdown
            return (
                <div className="form-group" key={paramKey}>
                    <label htmlFor={paramKey}>{label}{required ? ' *' : ''}</label>
                    <select
                        id={paramKey}
                        value={currentValue || ''}
                        onChange={(e) => handleTypeParamChange(paramKey, e.target.value)}
                    >
                        {param.enum_options.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {description && <small className="field-description">{description}</small>}
                </div>
            );
        } else if (paramType === 'decimal' || paramType === 'integer') {
            // Number fields
            const step = param.validation?.step || 'any';
            const min = param.validation?.min;
            const max = param.validation?.max;
            const prefix = param.ui_hints?.prefix;
            const suffix = param.ui_hints?.suffix;

            return (
                <div className="form-group" key={paramKey}>
                    <label htmlFor={paramKey}>{label}{required ? ' *' : ''}</label>
                    <div className="input-with-affixes">
                        {prefix && <span className="input-prefix">{prefix}</span>}
                        <input
                            type="number"
                            id={paramKey}
                            value={currentValue || ''}
                            onChange={(e) => handleTypeParamChange(paramKey, e.target.value)}
                            step={step}
                            min={min}
                            max={max}
                        />
                        {suffix && <span className="input-suffix">{suffix}</span>}
                    </div>
                    {description && <small className="field-description">{description}</small>}
                </div>
            );
        } else {
            // String fields
            return (
                <div className="form-group" key={paramKey}>
                    <label htmlFor={paramKey}>{label}{required ? ' *' : ''}</label>
                    <input
                        type="text"
                        id={paramKey}
                        value={currentValue || ''}
                        onChange={(e) => handleTypeParamChange(paramKey, e.target.value)}
                    />
                    {description && <small className="field-description">{description}</small>}
                </div>
            );
        }
    };

    // Форматирование имени параметра для отображения
    const formatParamName = (name) => {
        return name
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    if (!selectedPortfolio) {
        return (
            <div className="simulation-setup">
                <div className="loading-state">
                    <p>Загрузка информации о портфеле...</p>
                </div>
            </div>
        );
    }

    if (isLoadingTypes) {
        return (
            <div className="simulation-setup">
                <div className="loading-state">
                    <p>Загрузка типов симуляций...</p>
                </div>
            </div>
        );
    }

    if (errors.loadTypes) {
        return (
            <div className="simulation-setup">
                <div className="error-state">
                    <p>{errors.loadTypes}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="simulation-setup">
            <div className="simulation-setup__header">
                <h3>Создание симуляции для портфеля "{selectedPortfolio.name}"</h3>
                <p>Общая сумма портфеля: ${selectedPortfolio.total_value?.toLocaleString()}</p>
            </div>

            <form onSubmit={handleSubmit} className="simulation-setup__form">
                {/* Basic Settings */}
                <div className="form-section">
                    <h4>Основные параметры</h4>

                    <div className="form-group">
                        <label htmlFor="name">Название симуляции *</label>
                        <input
                            type="text"
                            id="name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Например: Консервативная стратегия 2020-2024"
                            className={errors.name ? 'error' : ''}
                        />
                        {errors.name && <span className="error-text">{errors.name}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">Описание</label>
                        <textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder="Краткое описание стратегии..."
                            rows={3}
                        />
                    </div>
                </div>

                {/* Simulation Type Tabs */}
                <div className="form-section">
                    <h4>Параметры симуляции</h4>

                    {/* Tabs for simulation types */}
                    <div className="simulation-tabs">
                        {simulationTypes.map(type => (
                            <button
                                key={type.id}
                                type="button"
                                className={`tab-button ${activeTab === type.id ? 'active' : ''}`}
                                onClick={() => setActiveTab(type.id)}
                            >
                                {type.name}
                            </button>
                        ))}
                    </div>

                    {/* Active tab content */}
                    <div className="tab-content">
                        {simulationTypes.map(type => (
                            activeTab === type.id && (
                                <div key={type.id} className="tab-panel">
                                    {type.description && (
                                        <p className="tab-description">{type.description}</p>
                                    )}

                                    {/* Dynamically generated form fields from parameters_schema */}
                                    <div className="dynamic-fields">
                                        {(() => {
                                            if (!type.parameters_schema || !type.parameters_schema.parameters) {
                                                return <p>No parameters available</p>;
                                            }

                                            const schema = type.parameters_schema;
                                            const allParams = schema.parameters;
                                            const groups = schema.groups || [];
                                            const renderedKeys = new Set();

                                            // Render groups
                                            const groupElements = groups
                                                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                                                .map(group => {
                                                    const groupParams = group.parameter_keys
                                                        .map(key => allParams.find(p => p.key === key))
                                                        .filter(p => p)
                                                        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

                                                    groupParams.forEach(p => renderedKeys.add(p.key));

                                                    return (
                                                        <div key={group.name} className="param-group">
                                                            <h5 className="group-title">{group.name}</h5>
                                                            {group.description && (
                                                                <p className="group-description">{group.description}</p>
                                                            )}
                                                            {groupParams.map(param => renderFormField(param))}
                                                        </div>
                                                    );
                                                });

                                            // Render remaining ungrouped parameters
                                            const remainingParams = allParams
                                                .filter(p => !renderedKeys.has(p.key))
                                                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

                                            const remainingElements = remainingParams.length > 0 ? (
                                                <div key="ungrouped" className="param-group">
                                                    <h5 className="group-title">Other Parameters</h5>
                                                    {remainingParams.map(param => renderFormField(param))}
                                                </div>
                                            ) : null;

                                            return [...groupElements, remainingElements];
                                        })()}
                                    </div>
                                </div>
                            )
                        ))}
                    </div>
                </div>

                {/* Submit Buttons */}
                <div className="form-actions">
                    {Object.keys(errors).length > 0 && !errors.submit && (
                        <div className="error-message" style={{ marginBottom: '15px' }}>
                            ⚠️ Заполните обязательные поля выше
                        </div>
                    )}

                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Создание...' : 'Создать симуляцию'}
                    </button>
                </div>

                {errors.submit && (
                    <div className="error-message">
                        {errors.submit}
                    </div>
                )}
            </form>
        </div>
    );
};

export default SimulationSetup;
